import React from 'react';
import { motion } from 'framer-motion';
import type { GameState, Player } from '../../types/bountifulSea';

// ═══════════════════════════════════════════════════════════════
// VICTORY SCREEN COMPONENT
// ═══════════════════════════════════════════════════════════════

interface VictoryScreenProps {
  state: GameState;
  onPlayAgain: () => void;
}

export const VictoryScreen: React.FC<VictoryScreenProps> = ({
  state,
  onPlayAgain,
}) => {
  const winner = state.winner ? state.players[state.winner] : null;
  const standings: Player[] = (state.finalStandings ?? [])
    .map((id) => state.players[id])
    .filter((p): p is Player => p !== undefined);

  return (
    <div className="bs-victory bs-content-box">
      {/* Crown */}
      <motion.div
        className="bs-victory-crown"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', duration: 0.8 }}
      >
        👑
      </motion.div>

      {/* Winner announcement */}
      <motion.h1
        className="bs-victory-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {winner?.name ?? 'No One'} Wins!
      </motion.h1>

      {winner && (
        <motion.p
          className="bs-victory-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{ color: winner.color }}
        >
          Final stash: {winner.stash} coins
        </motion.p>
      )}

      {/* Final standings */}
      <motion.div
        className="bs-standings"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <h3 style={{ textAlign: 'center', color: 'var(--bs-text-secondary)', marginBottom: '1rem' }}>
          Final Standings
        </h3>

        {standings.map((player, index) => {
          const rank = index + 1;
          const rankClass = rank <= 3 ? `bs-standing-rank-${rank}` : '';

          return (
            <motion.div
              key={player.id}
              className={`bs-standing-row ${rank === 1 ? 'bs-standing-row-first' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + index * 0.1 }}
            >
              <div className={`bs-standing-rank ${rankClass}`}>
                {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : rank}
              </div>
              <div className="bs-standing-name" style={{ color: player.color }}>
                {player.name}
                {player.isEliminated && (
                  <span style={{ color: 'var(--bs-text-muted)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                    (Shipwrecked)
                  </span>
                )}
              </div>
              <div className="bs-standing-stash">
                💰 {player.stash}
                <span style={{ color: 'var(--bs-text-muted)', marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                  🎲 {player.scoundrelDice}
                </span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Game stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        style={{ textAlign: 'center', color: 'var(--bs-text-muted)', marginTop: '1rem' }}
      >
        <p>
          Game completed in {state.roundHistory.length} rounds
        </p>
        <p>
          Total coins distributed: {standings.reduce((sum, p) => sum + p.stash, 0)}
        </p>
      </motion.div>

      {/* Play again button */}
      <motion.button
        className="bs-btn bs-btn-primary bs-btn-large"
        onClick={onPlayAgain}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Set Sail Again
      </motion.button>
    </div>
  );
};

export default VictoryScreen;
